import { fromEvent, Subject, EMPTY } from 'rxjs';
import { tap, filter, map } from 'rxjs/operators';
import matchesMobileUserAgent from 'modules/deviceCapabilities';
import SearchBarResultCategory from 'enums/searchResultCategory';
import CompletionSuggestionType from 'enums/completionSuggestionType';
import SearchBarComponent from "./searchBarComponent";
import SearchBarResult from "./searchBarResult";
import SearchBarService from "../services/searchBarService";
import SearchBarHelper from "../utils/searchBarHelper";
import setHeaderAndSaveButtonVisibility from '../utils/uiVisibilityHandler';

/**
 * Manages the search bar functionality, including input handling, displaying results,
 * and mobile-specific behaviors.
 * Utilizes RxJS for event handling and debouncing.
 */
export default class SearchBar {
  constructor(config) {
    this.subscriptions = [];
    this.searchResultsState = [];
    this.searchBarResult = new SearchBarResult();
    this.selectedResult$ = new Subject();
    this.selectedResultData = null;

    if (config) {
      this.isMobile = matchesMobileUserAgent();
      this.searchBarContainerDesktop = config.searchBarContainerDesktop;
      this.searchBarContainerMobile = config.searchBarContainerMobile;
      this.searchBarModalOpen = config.searchBarModalOpen;
      this.searchBarModal = config.searchBarModal;
      this.header = config.header;
      this.savedSearchContainer = config.savedSearchContainer;
      this.searchBarContainer = this.isMobile
        ? this.searchBarContainerMobile
        : this.searchBarContainerDesktop;

      this.initializeState();
      this.initializeElements();
      this.applySideEffects();
    }
  }

  /**
   * Initializes the search bar state with the provided search location name.
   *
   * @param {string} searchLocationName - The name of the search location to initialize the state with.
   */
  initializeState() {
    this.lastSearchTextFromCookie = SearchBarHelper.trimText(SearchBarHelper.getLastSearchText());
    this.searchText = this.lastSearchTextFromCookie;
  }

  /**
   * Initializes necessary DOM elements within the search bar container.
   */
  initializeElements() {
    const container = this.searchBarContainer;
    this.searchInput = container.querySelector('[data-search-input]') || null;
    this.resetButton = container.querySelector('[data-search-input-reset-button]') || null;
    this.searchBarHighlight = container.querySelector('[data-search-bar-input-container]') || null;
    this.currentLocation = container.querySelector('[data-current-location]') || null;
  }

  /**
   * Applies side effects such as initializing the search bar component,
   * subscribing to events, and binding mobile-specific behaviors.
   */
  applySideEffects() {
    this.toggleSearchBarsForDevice();
    this.initializeSearchBar();
    this.subscribeToEvents();
    this.bindMobileSearchBarEvents();
    this.setCurrentLocationOutsideClickListener();
    this.setCurrentLocationClickListener();
  }

  toggleSearchBarsForDevice() {
    this.searchBarContainerDesktop.hidden = this.isMobile;
    this.searchBarContainerMobile.hidden = !this.isMobile;
  }

  /**
   * Formats the selected search result data into a displayable string.
   * @param {Object} selectedResultData - The selected search result data.
   * @returns {string} The formatted search text.
   */
  getFormattedSearchText() {
    const formattedText = SearchBarHelper.getFormattedSearchText(this.selectedResultData);
    return formattedText || '';
  }

  /**
   * Checks if the currently selected search result is a location type.
   * @returns {boolean} True if the selected result is a location, false otherwise.
   */
  isLocationSelected() {
    return `${this.selectedResultData?.categoryType}` === SearchBarResultCategory.Location.id;
  }

  /**
   * Determines the last search text to display in the search input.
   * Prefers the selected location's value if applicable.
   * @returns {string} The last search text.
   */
  getLastSearchText() {
    return this.isLocationSelected() ? this.getFormattedSearchText() || ''
      : SearchBarHelper.trimText(this.searchText || '');
  }

  /**
   * Initializes the search bar component with necessary elements and last search text.
   */
  initializeSearchBar() {
    this.searchBarComponent = new SearchBarComponent();
    this.searchBarComponent.init({
      searchInput: this.searchInput,
      resetButton: this.resetButton,
      searchBarHighlight: this.searchBarHighlight,
      lastSearchText: this.getLastSearchText()
    });
  }

  /**
   * Subscribes to input change and focus events from the SearchBarComponent.
   */
  subscribeToEvents() {
    const inputChangeSubscription = this.searchBarComponent
      .getInputChangeObserver()
      .pipe(
        filter(searchText => searchText !== this.lastSearchTextFromCookie),
        tap(searchText => this.handleSearchTextChange(searchText))
      )
      .subscribe();

    const inputFocusSubscription = this.searchBarComponent
      .getInputFocusObserver()
      .pipe(
        tap(() => this.handleInputFocus())
      )
      .subscribe();

    this.searchResultsStateSubscription = SearchBarResult
      .getSelectedItemObservable(this.searchBarResult)
      .pipe(
        map((result) => {
          this.selectedResultData = result;
          return result;
        }),
        tap((result) => {
          if (
            result &&
            (
              result.categoryType === SearchBarResultCategory.Community.id ||
              result.categoryType === SearchBarResultCategory.Builder.id ||
              result.categoryType === SearchBarResultCategory.Article.id ||
              (result.categoryType === SearchBarResultCategory.Location.id &&
                result.completionSuggestionType === CompletionSuggestionType.State.name)
            )
          ) {
            document.body.dispatchEvent(new CustomEvent('search_result_non_srp_redirect', { detail: result }));
          }
          this.initializeSearchBar();
          this.handleMobileSearchClose();
        })
      )
      .subscribe((result) => this.selectedResult$?.next(result));

    this.subscriptions.push(inputChangeSubscription, inputFocusSubscription, this.searchResultsStateSubscription);
  }

  /**
   * Provides an observable for page refresh or redirect events.
   * @param {Object} context - The context containing the observable.
   * @returns {Observable} The page refresh or redirect observable.
   */
  getSelectedResultObservable() {
    return this.selectedResult$ ?? EMPTY;
  }

  /**
   * Handles changes in the search text input.
   * @param {string} searchText - The latest search text input by the user.
   */
  handleSearchTextChange(searchText) {
    this.searchTextChanged(searchText);
    this.processSearchResults(searchText);
    this.toggleSearchBarUI();
  }

  /**
   * Handles input focus events to update UI state.
   */
  handleInputFocus() {
    this.toggleSearchBarUI();
  }

  /**
   * Toggles the search bar UI elements based on current state.
   */
  toggleSearchBarUI() {
    this.toggleCurrentLocation();
    this.toggleSearchResultsMessageContainer();
  }

  /**
   * Updates the latest search text if the search input is available.
   * @param {string} searchText - The latest search text to update.
   */
  searchTextChanged(searchText) {
    this.searchText = searchText;
  }

  /**
   * Sets up a document-wide click listener to manage current location visibility.
   */
  setCurrentLocationOutsideClickListener() {
    if (!this.currentLocation || !this.searchBarContainer) { return; }

    const clickSubscription = fromEvent(document, 'click')
      .pipe(
        tap(event => this.handleDocumentClick(event))
      )
      .subscribe();

    this.subscriptions.push(clickSubscription);
  }

  /**
   * Checks if a click event target is inside relevant elements (current location or search bar input).
   * @param {HTMLElement} target - The target element of the click event.
   * @returns {boolean} - True if the click is inside relevant elements, false otherwise.
   */
  isClickInsideRelevantElements(target) {
    const isInsideCurrentLocation = this.currentLocation.contains(target);
    const searchBarInputContainer = this.searchBarContainer.querySelector('[data-search-bar-input-container]');
    const isInsideSearchBarInput = searchBarInputContainer?.contains(target);

    return isInsideCurrentLocation || isInsideSearchBarInput;
  }

  /**
   * Handles document click events to manage current location visibility and search results message.
   * @param {Event} event - The click event object.
   */
  handleDocumentClick(event) {
    const clickedInside = this.isClickInsideRelevantElements(event.target);

    if (!clickedInside) {
      this.hideCurrentLocation();
    }

    this.toggleSearchResultsMessageContainer();
  }

  /**
   * Hides the current location element.
   */
  hideCurrentLocation() {
    this.toggleCurrentLocation(true);
  }

  /**
   * Sets up a click listener on the current location element to handle mobile full-screen search modal close.
   * This ensures that clicking the current location also closes the mobile full-screen search bar modal.
   */
  setCurrentLocationClickListener() {
    if (!this.currentLocation) { return; }

    const clickSubscription = fromEvent(this.currentLocation, 'click')
      .pipe(
        tap(() => this.handleMobileSearchClose())
      )
      .subscribe();

    this.subscriptions.push(clickSubscription);
  }

  /**
   * Toggles the visibility of the search results message container on mobile devices.
   */
  toggleSearchResultsMessageContainer() {
    if (this.isMobile) {
      const container = this.searchBarContainer.querySelector('[data-search-results-info]');
      const searchKeywordContainer = container?.querySelector('[data-search-keyword-container]');

      const shouldDisplaySearchInstructions = this.shouldDisplaySearchInstructions();

      SearchBar.applyContainerVisibility(container, shouldDisplaySearchInstructions);
      SearchBar.applyContainerVisibility(searchKeywordContainer, false);
    }
  }

  /**
   * Determines if the search instructions should be displayed based on search text and current location visibility.
   * @returns {boolean} True if instructions should be displayed, false otherwise.
   */
  shouldDisplaySearchInstructions() {
    return SearchBarHelper.isNullOrWhiteSpace(this.searchText) && this.currentLocation.hidden;
  }

  static applyContainerVisibility(container, isVisible) {
    if (container) {
      container.hidden = !isVisible;
    }
  }

  /**
   * Toggles the visibility of the current location element based on search text and optional force hide flag.
   * @param {boolean} shouldForceHide - Whether to force the current location to be hidden.
   */
  toggleCurrentLocation(shouldForceHide = false) {
    const hide = SearchBarHelper.shouldHideCurrentLocation(this.searchText, shouldForceHide);
    this.applyCurrentLocationVisibility(hide);
  }

  /**
   * Applies the visibility state to the current location element.
   * @param {boolean} hidden - Whether to hide the current location element.
   */
  applyCurrentLocationVisibility(hidden) {
    this.currentLocation.hidden = hidden;
  }

  /**
   * Binds event listeners for mobile search bar functionality, including opening and closing the modal.
   */
  bindMobileSearchBarEvents() {
    if (!this.isMobile || !this.searchBarContainer) {
      return;
    }

    const closeButton = this.searchBarContainer.querySelector('[data-close-search-bar-modal]');

    this.searchBarModalOpen.hidden = false;
    if (this.searchBarContainerDesktop) {
      this.searchBarContainerDesktop.hidden = true;
    }

    // Subscribe to open button click
    this.subscriptions.push(
      fromEvent(this.searchBarModalOpen, 'click')
        .pipe(tap(() => this.handleMobileSearchOpen()))
        .subscribe()
    );

    const closeButtonClick$ = fromEvent(closeButton, 'click')
      .pipe(tap(() => this.handleMobileSearchClose()))
      .subscribe();

    // Subscribe to close button click
    this.subscriptions.push(closeButtonClick$);
  }

  /**
   * Handles the opening of the mobile search bar.
   * @param {HTMLElement} header - The main header element.
   * @param {HTMLElement} savedSearchContainer - The saved search container element.
   */
  handleMobileSearchOpen() {
    setHeaderAndSaveButtonVisibility(true, this.header, this.savedSearchContainer);
    this.toggleSearchResultsMessageContainer();
    this.toggleMobileSearchBarVisibility(true);
  }

  /**
   * Handles the closing of the mobile search bar.
   */
  handleMobileSearchClose() {
    if (this.isMobile) {
      setHeaderAndSaveButtonVisibility(false, this.header, this.savedSearchContainer);
      this.toggleMobileSearchBarVisibility(false);
    }
  }

  /**
   * Determines if the mobile search bar should be shown based on device type and input parameter.
   * @param {boolean} show - Whether to show the mobile search bar.
   * @returns {boolean} - True if the mobile search bar should be shown, false otherwise.
   */
  shouldShowMobileSearchBar(show) {
    return this.isMobile && this.searchBarContainer && show;
  }

  /**
   * Updates the visibility of the mobile search bar modal.
   * @param {boolean} show - Whether to show or hide the mobile search bar modal.
   */
  updateMobileSearchBarVisibility(show) {
    if (this.searchBarModal) {
      this.searchBarModal.hidden = !show;
    }
  }

  /**
   * Toggles the visibility of the mobile search bar modal.
   * @param {boolean} show - Whether to show or hide the mobile search bar modal.
   */
  toggleMobileSearchBarVisibility(show) {
    const visible = this.shouldShowMobileSearchBar(show);
    this.updateMobileSearchBarVisibility(visible);
  }

  /**
   * Processes the search text input by the user and fetches search results.
   * @param {string} searchText - The current search text input by the user.
   */
  async processSearchResults(searchText) {
    const trimmedSearchText = SearchBarHelper.trimText(searchText);

    if (!trimmedSearchText) {
      this.handleNoSearchText();
      return;
    }

    try {
      const searchResults = await SearchBarService.fetchSearchResults(trimmedSearchText);
      const results = SearchBarHelper.extractResults(searchResults);

      this.updateSearchState(results);
      this.displaySearchResults(trimmedSearchText, results.length ? results : this.searchResultsState);
    } catch {
      this.displaySearchResults(trimmedSearchText, this.searchResultsState);
    }
  }

  /**
   * Updates the internal search results state if new results are available.
   * @param {Array} results - The new search results to update the state with.
   */
  updateSearchState(results) {
    if (results.length) {
      this.searchResultsState = results;
    }
  }

  /**
   * Handles the scenario when there is no search text input.
   * Displays an empty search results state.
   */
  handleNoSearchText() {
    this.displaySearchResults('', []);
  }

  /**
   * Displays search results in the search bar results container.
   * @param {string} searchText - The current search text input by the user.
   * @param {Array} searchResults - The array of search results to display.
   */
  displaySearchResults(searchText, searchResults = []) {
    const resultsContainer = this.searchBarContainer.querySelector('[data-search-bar-result-container]');
    if (resultsContainer) {
      this.resetSearchResult();
      this.initializeSearchResult(resultsContainer, searchText, searchResults);
    }
  }

  /**
   * Resets the search results by destroying the existing SearchBarResult instance.
   */
  resetSearchResult() {
    if (this.searchBarResult) {
      this.searchBarResult.destroy();
    }
  }

  /**
   * Initializes the search results display with the provided container, search text, and results.
   * @param {HTMLElement} container - The container element for displaying search results.
   * @param {string} searchText - The current search text input by the user.
   * @param {Array} searchResults - The array of search results to display.
   */
  initializeSearchResult(container, searchText, searchResults) {
    this.searchBarResult.initialize({
      resultContainer: container,
      searchText,
      searchResults,
      isMobile: this.isMobile
    });
  }

  /** Cleans up subscriptions to prevent memory leaks. */
  destroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => subscription.unsubscribe());
      this.subscriptions = [];
    }

    if (this.searchBarComponent) {
      this.searchBarComponent.destroy();
      this.searchBarComponent = null;
    }
  }
}
